import React, { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { gameSubstitutionService, type GameSubstitution } from '../../services/gameSubstitutionService';
import type { Player } from '../../types';

/** Football sub-log entry form. */
interface AddSubstitutionModalProps {
  gameId: string;
  players: Player[];
  onClose: () => void;
  onAdded: (sub: GameSubstitution) => void;
}

export function AddSubstitutionModal({ gameId, players, onClose, onAdded }: AddSubstitutionModalProps) {
  const [playerOutId, setPlayerOutId] = useState<string>('');
  const [playerInId, setPlayerInId] = useState<string>('');
  const [quarter, setQuarter] = useState<string>('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const activePlayers = players.filter(
    (p) => !p.injury_status || p.injury_status === 'H' || p.injury_status === 'Q' || p.injury_status === 'D',
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const sub = await gameSubstitutionService.add({
        game_id: gameId,
        player_out_id: playerOutId || null,
        player_in_id: playerInId || null,
        quarter: quarter ? Number(quarter) : null,
        reason: reason.trim() || null,
      });
      onAdded(sub);
    } catch {
      toast.error('Failed to save substitution.');
    } finally {
      setSaving(false);
    }
  };

  const playerOption = (p: Player) =>
    `#${p.number ?? '—'} ${p.first_name ?? ''} ${p.last_name ?? ''}`.trim();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Log Substitution</h3>
          <button type="button" onClick={onClose} className="p-1 hover:bg-gray-100 rounded" aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} id="add-sub-form" className="p-4 space-y-4">
          <div>
            <label htmlFor="sub-out" className="block text-sm font-medium text-gray-700 mb-1">
              Player Out
            </label>
            <select
              id="sub-out"
              value={playerOutId}
              onChange={(e) => setPlayerOutId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">— Select player —</option>
              {activePlayers.map((p) => (
                <option key={p.id} value={p.id}>{playerOption(p)}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="sub-in" className="block text-sm font-medium text-gray-700 mb-1">
              Player In
            </label>
            <select
              id="sub-in"
              value={playerInId}
              onChange={(e) => setPlayerInId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">— Select player —</option>
              {activePlayers.map((p) => (
                <option key={p.id} value={p.id}>{playerOption(p)}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="sub-quarter" className="block text-sm font-medium text-gray-700 mb-1">
              Quarter (optional)
            </label>
            <select
              id="sub-quarter"
              value={quarter}
              onChange={(e) => setQuarter(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">—</option>
              <option value="1">Q1</option>
              <option value="2">Q2</option>
              <option value="3">Q3</option>
              <option value="4">Q4</option>
              <option value="5">OT</option>
            </select>
          </div>
          <div>
            <label htmlFor="sub-reason" className="block text-sm font-medium text-gray-700 mb-1">
              Reason (optional)
            </label>
            <input
              id="sub-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Injury, rotation, rest…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        </form>
        <div className="border-t border-gray-200 p-4 flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 btn-secondary py-2">Cancel</button>
          <button
            type="submit"
            form="add-sub-form"
            disabled={saving}
            className="flex-1 btn-primary py-2"
          >
            {saving ? 'Saving…' : 'Log Sub'}
          </button>
        </div>
      </div>
    </div>
  );
}
